import type { RenderBox, RenderGroupBox, RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import type { AlbumStrokeScope } from './frame-stroke-eligibility';
import type { PsdRenderWasmService } from './psd-render-wasm.service';
import type { SceneFrameStroke } from './spread-scene-adapter';
import type { MappedFace } from './text-scene-adapter';
import { runFontFaces, withRunFontRefs, type FontRefOf } from './run-fonts';

export interface RustSceneBuildInput {
  boxes: readonly RenderBox[];
  viewport: { width: number; height: number };
  renderScale: number;
  fonts: Record<string, Uint8Array>;
  fontRefOf?: FontRefOf;
  mappedFace?: (box: RenderTextBox) => MappedFace | undefined;
  frameStroke?: SceneFrameStroke;
  albumStrokeScope?: AlbumStrokeScope;
  /** Full spread when `boxes` is a gesture subset: the mask layer that cuts a lifted frame lives outside it. */
  maskLayers?: readonly RenderBox[];
}

/** The one build_scene wire model of the browser (editor canvas and stored preview alike) — the shape print sends. */
export function buildRustScene(wasm: Pick<PsdRenderWasmService, 'buildScene'>, input: RustSceneBuildInput): Promise<string> {
  const { frameStroke, albumStrokeScope: scope } = input;
  const model = {
    viewport: input.viewport,
    renderScale: input.renderScale,
    boxes: injectFontRefs(input.boxes, input.fontRefOf),
    // Ключа НЕТ у книги без дефолта — ровно та же форма модели, что печать шлёт в тот же адаптер.
    // На ОБЛОЖКЕ ключа нет тоже: запрет «обложке дефолт не положен» прямоугольником реза не
    // выражается, поэтому отправитель просто не шлёт дефолт — ровно как это делает печать.
    ...(frameStroke && !scope?.cover ? { frameStroke } : {}),
    // Печатная страница в книжных px: по ней адаптер решает, кому альбомный дефолт не положен
    // (рамка пересекает линию реза или лежит на ней). Ключа нет — запрета нет, как раньше.
    ...(scope && !scope.cover ? { trim: scope.trim } : {}),
    ...(input.maskLayers ? { maskLayers: input.maskLayers } : {}),
  };
  return wasm.buildScene(
    // Resources are registered by the caller beforehand. Keep only their IDs in the wire model,
    // including nested groups/maskLayers, rather than repeating LUT/profile bytes.
    JSON.stringify(model, (key, value) => (key === 'adjustmentResources' ? undefined : value)),
    input.fonts,
    collectMappedFaces(input.boxes, input.fontRefOf, input.mappedFace),
  );
}

// Tag each text box (and each run with its own font) with its layout font key (a `fonts` map key) so the Rust adapter
// shapes glyphs from the registered fonts; the host owns the key (option B in render_box.rs), the adapter never re-derives it.
function injectFontRefs(boxes: readonly RenderBox[], fontRefOf?: FontRefOf): RenderBox[] {
  if (!fontRefOf) return boxes as RenderBox[];
  const walk = (b: RenderBox): RenderBox => {
    if (b.type === 'text') {
      const tb = withRunFontRefs(b as RenderTextBox, fontRefOf);
      const ref = fontRefOf(tb);
      return ref ? ({ ...tb, fontRef: ref } as unknown as RenderBox) : tb;
    }

    if (b.type === 'group') {
      return { ...(b as RenderGroupBox), children: (b as RenderGroupBox).children.map(walk) } as RenderBox;
    }

    return b;
  };

  return boxes.map(walk);
}

const sameMappedFace = (a: MappedFace | undefined, b: MappedFace | undefined): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.targetPostScriptName === b.targetPostScriptName &&
    a.fontMetrics?.postScriptName === b.fontMetrics?.postScriptName &&
    a.fontMetrics?.weight === b.fontMetrics?.weight &&
    a.fontMetrics?.style === b.fontMetrics?.style);

// The instance each text box's mapping row names, by the same font key injectFontRefs tags the box with — the build_scene
// path reads it from the renderer's registry, as print reads it from its book font set. The key carries no mapping
// name, so two rows on one file and weight share it: when they disagree (different instances, or an instance and none)
// the key gets no instance and every such box draws the text's own weight, as a row without a target does.
function collectMappedFaces(
  boxes: readonly RenderBox[],
  fontRefOf?: FontRefOf,
  mappedFace?: (box: RenderTextBox) => MappedFace | undefined,
): Record<string, MappedFace> {
  const faces: Record<string, MappedFace> = {};
  if (!fontRefOf || !mappedFace) return faces;
  const claimed = new Map<string, MappedFace | undefined>();
  const contested = new Set<string>();
  const claim = (tb: RenderTextBox, face?: { derived: boolean }): void => {
    const ref = fontRefOf(tb, face);
    if (!ref) return;
    const mapped = mappedFace(tb);
    if (!claimed.has(ref)) claimed.set(ref, mapped);
    else if (!sameMappedFace(claimed.get(ref), mapped)) contested.add(ref);
  };

  const walk = (b: RenderBox): void => {
    if (b.type === 'text') {
      claim(b as RenderTextBox);
      runFontFaces(b as RenderTextBox).forEach(face => claim(face.box, face));
    } else if (b.type === 'group') {
      (b as RenderGroupBox).children.forEach(walk);
    }
  };

  boxes.forEach(walk);
  for (const [ref, face] of claimed) {
    if (face && !contested.has(ref)) faces[ref] = face;
  }

  return faces;
}
